import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgGridModule } from 'ag-grid-angular';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AddAssertionDialogComponent } from '../add-assertion/add-assertion-dialog.component';
import { ClaimsService } from '../../../core/services/claims.service';
import { GridService } from '../../../core/services/grid.service';
import type { IAssertionData } from '../../interfaces/assertion.interface';
import { AG_GRID_INPUTS } from '../../constants/ag-grid-inputs.constants';
import { DIALOG_CONFIGURATIONS_ASSERTION } from '../../constants/claims-ui-constants';
import * as viewAssertionRowdataInterface from '../../interfaces/view-assertion-rowdata.interface';

@Component({
  selector: 'emma-view-assertions-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AgGridModule,
    CdkDrag,
    CdkDragHandle
  ],
  templateUrl: './view-assertions-dialog.component.html',
  styleUrls: ['./view-assertions-dialog.component.scss']
})
export class ViewAssertionsDialogComponent implements OnInit {
  public dialogTitle = 'Assertions';
  public isMinimizeState = true;
  public rowData: Array<IAssertionData> = [];
  public columnDefs: Array<ColDef> = [];
  public readonly gridProperties = AG_GRID_INPUTS;
  public gridOptions: GridOptions = {
    suppressRowTransform: true,
    animateRows: true,
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    rowModelType: 'clientSide',
    getRowHeight: (params) => undefined,
    defaultColDef: {
      filter: false
    },
    overlayNoRowsTemplate: '<span>No assertions available</span>',
    onCellClicked: (event) => {
      const originalEvent = event.event as Event | undefined;
      const target = originalEvent?.target as HTMLElement | null;
      const actionElement = target?.closest('[data-action]') as HTMLElement | null;
      const action = actionElement?.getAttribute('data-action');
      const assertionId = actionElement?.getAttribute('data-id');
      if (!action || !assertionId) return;
      originalEvent?.stopPropagation();
      if (action === 'view') this.onViewAssertion(Number(assertionId));
      if (action === 'delete') this.onDeleteAssertion(Number(assertionId));
    }
  };
  private gridApi!: GridApi;

  constructor(
    public dialogRef: MatDialogRef<ViewAssertionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: viewAssertionRowdataInterface.IViewAssertionRowData,
    private readonly dialog: MatDialog,
    private readonly claimsService: ClaimsService,
    private readonly gridService: GridService
  ) {}

  public ngOnInit(): void {
    this.initializeGrid();
    this.loadAssertions();
  }

  private initializeGrid(): void {
    this.columnDefs = this.gridService.getClaimAssertionHeaders();
  }

  private loadAssertions(): void {
    this.rowData = (this.data.assertions || []).map(assertion => ({
      ...assertion,
      assertionId: assertion.assertionId || (assertion as any).id
    }));
  }

  public onGridReady(params: any): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  public onViewAssertion(assertionId: number): void {
    const assertion = this.rowData.find(a => a.assertionId === assertionId);
    if (!assertion) return;

    const dialogData = {
      mode: 'view',
      assertionData: assertion
    };

    this.dialog.open(AddAssertionDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxHeight: '90vh'
    });
  }

  public onDeleteAssertion(assertionId: number): void {
    const dialogRef = this.dialog.open(
      ConfirmationDialogComponent, 
      DIALOG_CONFIGURATIONS_ASSERTION.DELETE_CLAIM_ASSERTION
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'delete') {
        this.performDelete(assertionId);
      }
    });
  }

  private performDelete(assertionId: number): void {
    this.claimsService.deleteAssertions([assertionId]).subscribe({
      next: () => {
        this.removeAssertionFromGrid(assertionId);
      }
    });

    this.removeAssertionFromGrid(assertionId);
  }

  private removeAssertionFromGrid(assertionId: number): void {
    this.rowData = this.rowData.filter(assertion => assertion.assertionId !== assertionId);
    if (this.gridApi) {
      this.gridApi.setRowData(this.rowData);
    }
  }
  public onClose(): void {
    this.dialogRef.close();
  }

  public restoreState(): void {
    this.isMinimizeState = !this.isMinimizeState;
  }
}
